/**
 * "Add competitor" CTA. POSTs to /v1/competitors with inline field-level
 * errors and friendly domain normalization (strips protocol/www/path).
 */
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Globe, PlusCircle } from 'lucide-react';
import { toast } from 'sonner';
import type { Competitor, Project } from '@seo/types';
import { Button, type ButtonProps } from '@/components/ui/button';
import { FormDialog, FormDialogBlock } from '@/components/ui/form-dialog';
import { TextField, FormField } from '@/components/ui/form-field';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { extractFieldErrors, summarizeApiError } from '@/lib/api';
import { useCreateCompetitor } from '@/features/competitors/use-create-competitor';

const BARE_DOMAIN_RE =
  /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/;

const CompetitorFormSchema = z.object({
  project_id: z.string().uuid({ message: 'Pick a project' }),
  domain: z
    .string()
    .trim()
    .min(3, 'Required')
    .max(253, 'Too long')
    .transform(normalizeDomain)
    .refine((v) => BARE_DOMAIN_RE.test(v), {
      message: 'Use a bare domain like moz.com — no protocol, www, or path',
    }),
  label: z.string().trim().max(120, 'Too long').optional(),
});
type CompetitorFormValues = z.infer<typeof CompetitorFormSchema>;

export interface CompetitorCreateButtonProps {
  projects: Project[];
  defaultProjectId?: string;
  label?: string;
  buttonProps?: Omit<ButtonProps, 'onClick'>;
  onCreated?: (c: Competitor) => void;
}

export function CompetitorCreateButton({
  projects,
  defaultProjectId,
  label = 'Add competitor',
  buttonProps,
  onCreated,
}: CompetitorCreateButtonProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const trigger = (
    <Button {...buttonProps}>
      <PlusCircle className="h-4 w-4" />
      {label}
    </Button>
  );

  if (projects.length === 0) {
    return (
      <FormDialogBlock
        open={open}
        onOpenChange={setOpen}
        trigger={trigger}
        title="No projects yet"
        description="Create a project first — competitors live inside a project."
      />
    );
  }

  return (
    <CompetitorDialog
      open={open}
      onOpenChange={setOpen}
      trigger={trigger}
      projects={projects}
      defaultProjectId={defaultProjectId}
      onCreated={onCreated}
    />
  );
}

function CompetitorDialog({
  open,
  onOpenChange,
  trigger,
  projects,
  defaultProjectId,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  trigger: JSX.Element;
  projects: Project[];
  defaultProjectId?: string;
  onCreated?: (c: Competitor) => void;
}): JSX.Element {
  const create = useCreateCompetitor();
  const initialProjectId =
    defaultProjectId ?? (projects.length === 1 ? projects[0]?.id : '') ?? '';

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    setError,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<CompetitorFormValues>({
    resolver: zodResolver(CompetitorFormSchema),
    defaultValues: { project_id: initialProjectId, domain: '', label: '' },
    mode: 'onBlur',
  });

  const projectId = watch('project_id');
  const domainPreview = watch('domain');

  useEffect(() => {
    if (!projectId && projects.length === 1) {
      setValue('project_id', projects[0]!.id, { shouldValidate: false });
    }
  }, [projectId, projects, setValue]);

  const submit = handleSubmit(async (values) => {
    try {
      const created = await create.mutateAsync({
        project_id: values.project_id,
        domain: values.domain,
        ...(values.label ? { label: values.label } : {}),
      });
      toast.success('Competitor added', { description: created.domain });
      reset();
      onOpenChange(false);
      onCreated?.(created);
    } catch (e) {
      const fieldErrors = extractFieldErrors(e);
      for (const f of ['domain', 'project_id', 'label'] as const) {
        if (fieldErrors[f]) setError(f, { type: 'server', message: fieldErrors[f] });
      }
      const summary = summarizeApiError(e, {
        fallbackTitle: 'Could not add competitor',
        fieldLabels: { domain: 'Domain', project_id: 'Project', label: 'Label' },
      });
      if (summary.code === 'CONFLICT' && !fieldErrors.domain) {
        setError('domain', {
          type: 'server',
          message: 'You already track this competitor.',
        });
      }
      if (summary.code === 'NOT_FOUND' && !fieldErrors.project_id) {
        setError('project_id', { type: 'server', message: summary.description });
      }
      toast.error(summary.title, { description: summary.description });
    }
  });

  const domainHint =
    domainPreview && domainPreview.includes('/')
      ? `We'll save this as "${normalizeDomain(domainPreview)}".`
      : 'Just the bare domain — no https://, no www, no trailing slash.';

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      trigger={trigger}
      icon={<Globe className="h-5 w-5 text-primary" />}
      title="Track a new competitor"
      description="We'll diff their sitemap on every scan, classify new pages, and surface rank gaps."
      submitLabel="Add competitor"
      submitting={isSubmitting || create.isPending}
      onSubmit={submit}
    >
      <FormField label="Project" error={errors.project_id?.message}>
        <Select
          value={projectId}
          onValueChange={(v) => setValue('project_id', v, { shouldValidate: true })}
        >
          <SelectTrigger>
            <SelectValue placeholder="Pick a project" />
          </SelectTrigger>
          <SelectContent>
            {projects.map((p) => (
              <SelectItem key={p.id} value={p.id}>
                {p.name} — {p.domain.replace(/^https?:\/\//, '').replace(/\/$/, '')}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FormField>

      <TextField
        label="Domain"
        placeholder="moz.com"
        autoFocus
        autoComplete="off"
        spellCheck={false}
        error={errors.domain?.message}
        hint={domainHint}
        {...register('domain')}
      />

      <TextField
        label="Label (optional)"
        placeholder="e.g. Direct competitor"
        autoComplete="off"
        hint="Just a note to remind you why you're tracking them."
        error={errors.label?.message}
        {...register('label')}
      />
    </FormDialog>
  );
}

function normalizeDomain(raw: string): string {
  let s = raw.trim().toLowerCase();
  s = s.replace(/^https?:\/\//, '');
  s = s.replace(/^www\./, '');
  s = s.split('/')[0] ?? s;
  s = s.split(':')[0] ?? s;
  return s;
}
